import { AsyncPipe, DecimalPipe } from '@angular/common'
import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { MatButtonModule, MatIconButton } from '@angular/material/button'
import { MatDialog } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatMenuModule } from '@angular/material/menu'
import { MatTooltip } from '@angular/material/tooltip'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { MatIconButtonSizesModule } from 'mat-icon-button-sizes'
import { map, Observable, of, Subscription, tap } from 'rxjs'
import type { CitationMinuteNodes } from '../../../../../backend/src/citations/minutes-citations-api.dto'
import { Meeting, MeetingsService, canDownloadMinutes, hasEditAccess, MetricType } from '../../meetings.service'
import { InfoDialogComponent } from '../../info-dialog/info-dialog.component'
import { MetricsService } from '../../metrics.service'
import { ToastService } from '../../toast.service'
import { EditorSettingsService, EDITOR_ZOOM_MAX, EDITOR_ZOOM_MIN } from '../editor-settings.service'
import { LegacyMinutesNoticeComponent } from '../legacy-minutes-notice/legacy-minutes-notice.component'
import { OutlineComponent } from '../outline/outline.component'
import { ActionItemsComponent } from '../action-items/action-items.component'
import { ActionItemsOverview, ActionItemsService } from '../action-items/action-items.service'
import { hasActionItemsTab } from '../action-items/action-items-view'
import { OpenDocumentEvent, OpenMeetingEvent } from '../tiptap-editor/extensions/citation-plugin'
import { TiptapEditorComponent } from '../tiptap-editor/tiptap-editor.component'
import { RevisionSummary } from '../tiptap-editor/editor-revisions.service'
import { RestrictedMinutesComponent } from '../tiptap-editor/restricted-minutes/restricted-minutes.component'
import { ProgressLoaderComponent } from '../../common/progress-loader/progress-loader.component'
import { MeetingEntitiesOverview, MeetingEntitiesService, MeetingEntity } from '../meeting-entities/meeting-entities.service'
import { MeetingEntityReadiness, resolveMeetingEntityReadiness } from '../meeting-entities/meeting-entity-readiness'
import {
  ManageMeetingEntitiesDialogComponent,
  ManageMeetingEntitiesDialogData,
} from '../meeting-entities/manage-meeting-entities-dialog/manage-meeting-entities-dialog.component'
import { MinutesDocumentSwitcherComponent } from '../meeting-entities/minutes-document-switcher/minutes-document-switcher.component'

@Component({
  selector: 'app-meeting-outputs-container',
  standalone: true,
  imports: [
    LegacyMinutesNoticeComponent,
    OutlineComponent,
    ActionItemsComponent,
    TiptapEditorComponent,
    RestrictedMinutesComponent,
    MatIcon,
    MatIconButton,
    TranslatePipe,
    TranslateDirective,
    MatTooltip,
    MatMenuModule,
    MatButtonModule,
    AsyncPipe,
    DecimalPipe,
    MatIconButtonSizesModule,
    MinutesDocumentSwitcherComponent,
    ProgressLoaderComponent,
  ],
  templateUrl: './meeting-outputs-container.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './meeting-outputs-container.component.scss',
})
export class MeetingOutputsContainerComponent implements OnChanges, OnDestroy {
  @ViewChild(TiptapEditorComponent)
  tiptapEditor: TiptapEditorComponent | null = null

  @ViewChild(OutlineComponent)
  outlineComponent: OutlineComponent | undefined

  @ViewChild(RestrictedMinutesComponent)
  restrictedMinutes: RestrictedMinutesComponent | null = null

  /** Whichever minutes view is showing: the editor, or a Restricted user's sections. Both search their own content. */
  get findReplaceHost(): { findReplaceOpen$: Observable<boolean>; toggleFindReplace(): void } | null {
    return this.tiptapEditor ?? this.restrictedMinutes
  }

  @Input({ required: true })
  meeting!: Meeting

  @Input({ required: true })
  canUserEdit!: boolean

  @Input({ required: true })
  canUserSuggest!: boolean

  @Input({ required: true })
  canUserComment!: boolean

  @Input({ required: true })
  canResolveAnyThread!: boolean

  @Input()
  isSensitiveModeEnabled: boolean = false

  @Output()
  openAgendaUpload = new EventEmitter<void>()

  @Output()
  openMeeting = new EventEmitter<OpenMeetingEvent>()

  @Output()
  openDocument = new EventEmitter<OpenDocumentEvent>()

  get showExpandAll(): boolean {
    return this.outlineComponent?.showExpandAll() ?? false
  }

  currentTab: 'EDITOR' | 'OUTLINE' | 'ACTIONS' = 'EDITOR'

  get showCitations(): boolean {
    return this.editorSettingsService.showCitations
  }

  set showCitations(value: boolean) {
    this.editorSettingsService.setShowCitations(value)
  }

  get hasCitations(): boolean {
    if (this.isRestricted) {
      return false
    }
    return this.selectedMeetingEntityId === null ? this.meeting.minutesCitations !== null : this.latestEntityCitations !== null
  }

  /**
   * A Restricted user sees only their own items, so the whole-minutes tools (revisions, word count, export) are hidden,
   * and nothing behind the minutes either — no citations, which point into the recording and the papers.
   */
  get isRestricted(): boolean {
    return this.meeting.accessLevel === 'RESTRICTED'
  }

  /** Action items are their own records, so they stay editable while the minutes are still being generated. */
  get canUserEditActionItems(): boolean {
    return hasEditAccess(this.meeting)
  }

  get canExportCurrentTab(): boolean {
    if (this.isRestricted || this.currentTab === 'ACTIONS') {
      return false
    }
    if (this.currentTab === 'OUTLINE') {
      return this.meeting.allowOutlineDownload
    }
    const selectedEntity = this.selectedEntity
    if (selectedEntity !== null) {
      return canDownloadMinutes(this.meeting) && selectedEntity.hasMinutes
    }
    if (!this.meeting.hasEditorDocument) {
      return false
    }
    // With no minutes the editor content is seeded from the outline, so exporting it
    // requires outline download permission (mirrors the backend download guard).
    return canDownloadMinutes(this.meeting) && (this.meeting.hasMinutes || this.meeting.allowOutlineDownload)
  }

  toggleCitations() {
    this.editorSettingsService.setShowCitations(!this.showCitations)
  }

  get showWordCount(): boolean {
    return this.editorSettingsService.showWordCount
  }

  toggleWordCount() {
    this.editorSettingsService.setShowWordCount(!this.showWordCount)
  }

  toggleEditorMaximized() {
    this.editorSettingsService.setIsEditorMaximized(!this.editorSettingsService.isEditorMaximized)
  }

  readonly editorZoomMin = EDITOR_ZOOM_MIN
  readonly editorZoomMax = EDITOR_ZOOM_MAX

  zoomIn() {
    this.editorSettingsService.zoomIn()
  }

  zoomOut() {
    this.editorSettingsService.zoomOut()
  }

  resetZoom() {
    this.editorSettingsService.resetZoom()
  }

  relayOpenDocument(event: OpenDocumentEvent) {
    this.editorSettingsService.setIsEditorMaximized(false)
    this.openDocument.emit(event)
  }

  relayOpenMeeting(event: OpenMeetingEvent) {
    this.editorSettingsService.setIsEditorMaximized(false)
    this.openMeeting.emit(event)
  }

  toastService = inject(ToastService)
  dialog = inject(MatDialog)
  translateService = inject(TranslateService)
  metricsService = inject(MetricsService)
  meetingsService = inject(MeetingsService)
  editorSettingsService = inject(EditorSettingsService)
  meetingEntitiesService = inject(MeetingEntitiesService)
  actionItemsService = inject(ActionItemsService)
  showActionsTab = false
  private actionItemsSubscription: Subscription | null = null
  private latestActionItems: ActionItemsOverview | null = null
  isEditorMaximized$ = this.editorSettingsService.isEditorMaximized$
  zoomLevel$ = this.editorSettingsService.zoomLevel$

  entitiesOverview$: Observable<MeetingEntitiesOverview | null> = of(null)
  entityCitations$: Observable<CitationMinuteNodes | null> = of(null)
  selectedMeetingEntityId: number | null = null
  private latestOverview: MeetingEntitiesOverview | null = null
  private latestEntityCitations: CitationMinuteNodes | null = null
  private generateEntityMinutesMutation = this.meetingEntitiesService.generateEntityMinutesMutation()

  ngOnChanges(changes: SimpleChanges) {
    const change = changes['meeting']
    if (change === undefined) {
      return
    }
    const previousMeeting = change.firstChange ? null : (change.previousValue as Meeting)
    if (previousMeeting === null || previousMeeting.id !== this.meeting.id) {
      this.watchActionItems()
    } else {
      this.followActionItemDetection()
    }
    if (previousMeeting !== null && previousMeeting.id === this.meeting.id && previousMeeting.allowMeetingEntities === this.meeting.allowMeetingEntities) {
      return
    }
    this.latestOverview = null
    this.selectDocument(null)
    this.entitiesOverview$ = this.meeting.allowMeetingEntities
      ? this.meetingEntitiesService.getOverview(this.meeting.id).pipe(
          map((result) => result.data ?? null),
          tap((overview) => this.keepSelectionWithin(overview))
        )
      : of(null)
  }

  get selectedEntity(): MeetingEntity | null {
    return this.latestOverview?.entities.find((entity) => entity.id === this.selectedMeetingEntityId) ?? null
  }

  selectedEntityReadiness(overview: MeetingEntitiesOverview, entity: MeetingEntity): MeetingEntityReadiness {
    return resolveMeetingEntityReadiness(overview, entity)
  }

  selectDocument(meetingEntityId: number | null) {
    this.selectedMeetingEntityId = meetingEntityId
    this.latestEntityCitations = null
    this.entityCitations$ =
      meetingEntityId === null
        ? of(null)
        : this.meetingEntitiesService.getCitations(this.meeting.id, meetingEntityId).pipe(
            map((result) => result.data?.citations ?? null),
            tap((citations) => {
              this.latestEntityCitations = citations
            })
          )
  }

  openManageEntities() {
    this.dialog.open<ManageMeetingEntitiesDialogComponent, ManageMeetingEntitiesDialogData>(ManageMeetingEntitiesDialogComponent, {
      data: { meetingId: this.meeting.id },
      width: '760px',
      maxWidth: '95vw',
      maxHeight: '90vh',
    })
  }

  async generateSelectedEntityMinutes() {
    if (this.selectedMeetingEntityId === null) {
      return
    }
    await this.generateEntityMinutesMutation.mutateAsync({ meetingId: this.meeting.id, meetingEntityId: this.selectedMeetingEntityId })
  }

  /** An entity deleted elsewhere takes the reader back to the whole meeting rather than to a document that is gone. */
  private keepSelectionWithin(overview: MeetingEntitiesOverview | null) {
    this.latestOverview = overview
    const isSelectionGone = this.selectedMeetingEntityId !== null && (overview?.entities.every((entity) => entity.id !== this.selectedMeetingEntityId) ?? true)
    if (isSelectionGone) {
      this.selectDocument(null)
    }
  }

  ngOnDestroy() {
    this.actionItemsSubscription?.unsubscribe()
  }

  /**
   * The Actions tab follows action item detection: it shows from the end of the meeting, while the processing that
   * leads to detection runs, and stays once detection has run or items exist.
   */
  private watchActionItems() {
    this.actionItemsSubscription?.unsubscribe()
    this.showActionsTab = false
    this.latestActionItems = null
    this.actionItemsSubscription = this.actionItemsService.list(this.meeting.id).subscribe((result) => {
      this.latestActionItems = result.data ?? null
      this.updateActionsTabVisibility()
    })
  }

  /** The list only polls while detection runs, so until it starts, each update of the meeting re-fetches it. */
  private followActionItemDetection() {
    if (this.latestActionItems === null) {
      return
    }
    if (this.latestActionItems.detectionStatus === 'NOT_STARTED') {
      this.actionItemsService.refresh(this.meeting.id)
    }
    this.updateActionsTabVisibility()
  }

  private updateActionsTabVisibility() {
    this.showActionsTab = this.latestActionItems !== null && hasActionItemsTab(this.latestActionItems, this.meeting)
    if (!this.showActionsTab && this.currentTab === 'ACTIONS') {
      this.currentTab = 'EDITOR'
    }
  }

  switchTab(tab: 'EDITOR' | 'OUTLINE' | 'ACTIONS') {
    this.currentTab = tab
    if (tab === 'OUTLINE') {
      this.metricsService.sendMetric(this.meeting.id, MetricType.CLICK_ON_OUTLINE_TAB)
    } else if (tab === 'ACTIONS') {
      this.metricsService.sendMetric(this.meeting.id, MetricType.CLICK_ON_ACTIONS_TAB)
    }
  }

  async exportDocument(format: 'pdf' | 'docx', revision: RevisionSummary | null = null) {
    const contentType = this.currentTab === 'EDITOR' ? 'minutes' : 'outline'
    const infoDialogRef = this.dialog.open(InfoDialogComponent, {
      width: '400px',
      data: {
        title: this.translateService.instant(contentType === 'minutes' ? 'MEETINGS.MINUTES_EXPORT_DIALOG_TITLE' : 'MEETINGS.OUTLINE_EXPORT_DIALOG_TITLE'),
        displayLoadingSpinner: true,
        displayConfirmButton: false,
      },
    })
    const selectedEntity = this.selectedEntity
    const export$ =
      contentType === 'minutes'
        ? this.meetingsService.exportMinutes(
            this.meeting,
            format,
            selectedEntity === null ? null : { id: selectedEntity.id, name: selectedEntity.name },
            revision
          )
        : this.meetingsService.exportOutline(this.meeting, format)
    const subscription = export$.subscribe({
      error: () => {
        this.toastService.show('MEETINGS.EXPORT_FAILED')
        infoDialogRef.close()
      },
      complete: () => {
        infoDialogRef.close()
      },
    })
    infoDialogRef.afterClosed().subscribe(() => {
      subscription.unsubscribe()
    })
  }

  expandAll() {
    this.outlineComponent?.expandAll()
  }

  collapseAll() {
    this.outlineComponent?.collapseAll()
  }
}
